import { ArtifactProto } from "build-ts/chat_session";
import { ExecutionStepProto } from "build-ts/chat_session";
import { ScheduledJobProto } from "build-ts/schedule";
import { ArtifactOpenOpts, ChatMessage } from "../interfaces";
import React, { useMemo } from "react";
import { Box, Icon, Link } from "@chakra-ui/react";
import { ArtifactLink } from "./ArtifactLink";
import ReactMarkdown from "react-markdown";
import { AppIcons } from "constants/icons";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import {
  escapeCurrencyDollar,
  findChartArtifactByName,
  findCodeArtifactByName,
  findKGDataframeArtifactByTableName,
  findReportArtifactByName,
  findWorkflowArtifactByDarwinName,
  normalizeLatex,
  transformAnalysisLinks,
  transformChartLinks,
  transformCodeLinks,
  transformDataframeLinks,
  transformReportLinks,
} from "modules/artifacts/utils/markdownLinkUtils";
import {
  ArtifactKind,
  getArtifactOpenOpts,
  getDataframeTagLinkPresentation,
  getOpenOptsForResolvedArtifact,
} from "modules/artifacts/utils/artifactUtils";
import { ARTIFACT_TAG_TYPE } from "modules/chat/utils/chatUtils";

// ─── Decode helper ────────────────────────────────────────────────────────────

function decodeHrefName(encoded: string): string {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

/**
 * Markdown Renderer
 */
export const MarkdownRenderer: React.FC<{
  content: string;
  artifacts?: ArtifactProto[];
  executionSteps?: ExecutionStepProto[];
  isArtifactsPanelVisible?: boolean;
  fontSize?: string;
  /** When true, renders all text in a muted grey — used for reasoning/thought blocks. */
  isReasoning?: boolean;
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  chatMessages?: ChatMessage[];
  scenarioJobs?: ScheduledJobProto[];
}> = ({
  content,
  artifacts,
  executionSteps,
  isArtifactsPanelVisible,
  fontSize,
  isReasoning = false,
  lookupByName,
  onArtifactClick,
  chatMessages,
  scenarioJobs,
}) => {
  const fs = useMemo(() => {
    if (fontSize) {
      return {
        base: fontSize,
        h1: fontSize,
        h2: fontSize,
        h3: fontSize,
        h4: fontSize,
        code: fontSize,
      };
    }
    return {
      base: isArtifactsPanelVisible ? "0.85rem" : "1rem",
      h1: isArtifactsPanelVisible ? "1rem" : "1.25rem",
      h2: isArtifactsPanelVisible ? "0.875rem" : "1.125rem",
      h3: isArtifactsPanelVisible ? "0.85rem" : "1rem",
      h4: isArtifactsPanelVisible ? "0.75rem" : "0.875rem",
      code: isArtifactsPanelVisible ? "0.8rem" : "0.875rem",
    };
  }, [isArtifactsPanelVisible, fontSize]);

  const textColor = isReasoning ? "var(--chakra-colors-gray-500)" : undefined;

  // Transform all custom link types to standard markdown links, then normalise LaTeX.
  const normalizedContent = useMemo(
    () =>
      normalizeLatex(
        escapeCurrencyDollar(
          transformAnalysisLinks(
            transformReportLinks(transformCodeLinks(transformChartLinks(transformDataframeLinks(content))))
          )
        )
      ),
    [content]
  );

  // Only remount markdown when structural metadata changes (artifacts/steps),
  // not on token text growth; remounting on every content length change is expensive.
  const markdownRemountKey = useMemo(
    () => [executionSteps?.length ?? 0, artifacts?.length ?? 0].join("-"),
    [executionSteps?.length, artifacts?.length]
  );

  // Memoize renderers to avoid rebuilding the entire components map every render.
  const markdownComponents = useMemo<any>(() => {
    const fallbackOpts = { executionSteps, messageArtifacts: artifacts, chatMessages };

    /** Single generic helper for all four artifact link types. */
    function renderArtifactLink(
      artifact: ArtifactProto | undefined,
      icon: React.ElementType,
      iconSize: number,
      label: React.ReactNode,
      opts?: ArtifactOpenOpts
    ) {
      return (
        <ArtifactLink isActive={!!artifact} onClick={() => artifact && onArtifactClick?.(artifact, opts)}>
          <Icon as={icon} flexShrink={0} boxSize={iconSize} />
          {label}
        </ArtifactLink>
      );
    }

    // Render external link
    function renderExternalLink(href: string | undefined, children: React.ReactNode) {
      return (
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "var(--chakra-colors-blue-400)",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {children}
        </Link>
      );
    }

    return {
      ul: ({ node, ...props }: any) => (
        <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
      ),
      ol: ({ node, ...props }: any) => (
        <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
      ),
      li: ({ node, ...props }: any) => (
        <li
          style={{
            fontSize: fs.base,
            marginBottom: "0.15rem",
            fontWeight: 500,
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      p: ({ node, ...props }: any) => (
        <p
          style={{
            fontSize: fs.base,
            marginTop: "0.5rem",
            marginBottom: "0.5rem",
            fontWeight: 500,
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      strong: ({ node, ...props }: any) => <strong style={{ fontWeight: 650 }} {...props} />,
      h1: ({ node, ...props }: any) => (
        <h1
          style={{
            fontSize: fs.h1,
            fontWeight: "bold",
            margin: "1rem 0",
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      h2: ({ node, ...props }: any) => (
        <h2
          style={{
            fontSize: fs.h2,
            fontWeight: "bold",
            margin: "0.875rem 0",
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      h3: ({ node, ...props }: any) => (
        <h3
          style={{
            fontSize: fs.h3,
            fontWeight: "bold",
            margin: "0.75rem 0",
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      h4: ({ node, ...props }: any) => (
        <h4
          style={{
            fontSize: fs.h4,
            fontWeight: "bold",
            margin: "0.625rem 0",
            ...(textColor && { color: textColor }),
          }}
          {...props}
        />
      ),
      a: ({ href, children }: any) => {
        if (href?.startsWith(`#${ARTIFACT_TAG_TYPE.dataframe}:`)) {
          const name = decodeHrefName(href.replace(`#${ARTIFACT_TAG_TYPE.dataframe}:`, ""));
          const artifact =
            lookupByName?.(name, ARTIFACT_TAG_TYPE.dataframe) ?? findKGDataframeArtifactByTableName(name, fallbackOpts);
          const { icon, opts } = getDataframeTagLinkPresentation(artifact, name);
          return renderArtifactLink(artifact, icon, 4, children, opts);
        }
        if (href?.startsWith(`#${ARTIFACT_TAG_TYPE.workflow}:`)) {
          const name = decodeHrefName(href.replace(`#${ARTIFACT_TAG_TYPE.workflow}:`, ""));
          const artifact = findWorkflowArtifactByDarwinName(name, { ...fallbackOpts, scenarioJobs });
          const label = artifact?.scenario_job?.header?.name || name;
          return renderArtifactLink(artifact, AppIcons.workflow, 5, label);
        }
        if (href?.startsWith(`#${ARTIFACT_TAG_TYPE.chart}:`)) {
          const name = decodeHrefName(href.replace(`#${ARTIFACT_TAG_TYPE.chart}:`, ""));
          const artifact = lookupByName?.(name, ARTIFACT_TAG_TYPE.chart) ?? findChartArtifactByName(name, fallbackOpts);
          return renderArtifactLink(
            artifact,
            AppIcons.chart,
            4,
            children,
            getOpenOptsForResolvedArtifact(artifact, ARTIFACT_TAG_TYPE.chart, name)
          );
        }
        if (href?.startsWith(`#${ARTIFACT_TAG_TYPE.code}:`)) {
          const name = decodeHrefName(href.replace(`#${ARTIFACT_TAG_TYPE.code}:`, ""));
          const artifact = lookupByName?.(name, ARTIFACT_TAG_TYPE.code) ?? findCodeArtifactByName(name, fallbackOpts);
          return renderArtifactLink(
            artifact,
            AppIcons.codeAlt,
            4,
            children,
            getArtifactOpenOpts(ARTIFACT_TAG_TYPE.code, name)
          );
        }
        if (href?.startsWith(`#${ARTIFACT_TAG_TYPE.report}:`)) {
          const name = decodeHrefName(href.replace(`#${ARTIFACT_TAG_TYPE.report}:`, ""));
          const artifact =
            lookupByName?.(name, ARTIFACT_TAG_TYPE.report) ?? findReportArtifactByName(name, fallbackOpts);
          return renderArtifactLink(
            artifact,
            AppIcons.document,
            4,
            children,
            getOpenOptsForResolvedArtifact(artifact, ARTIFACT_TAG_TYPE.report, name)
          );
        }
        return renderExternalLink(href, children);
      },
      pre: ({ children }: any) => (
        <pre
          style={{
            backgroundColor: "bg.surface",
            color: "text.primary",
            padding: "0 0 0 1rem",
            borderRadius: "8px",
            overflowX: "auto",
            margin: "0.75rem 0",
            fontSize: fs.code,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {children}
        </pre>
      ),
      table: ({ children }: any) => (
        <div style={{ overflowX: "auto", marginTop: "1rem", marginBottom: "1rem", maxWidth: "100%" }}>
          <table
            style={{
              borderCollapse: "collapse",
              border: "1px solid #d1d5db",
              minWidth: "100%",
              whiteSpace: "nowrap",
              fontSize: fs.code,
            }}
          >
            {children}
          </table>
        </div>
      ),
      thead: ({ children }: any) => <thead style={{ backgroundColor: "#f3f4f6" }}>{children}</thead>,
      th: ({ children }: any) => (
        <th
          style={{
            border: "1px solid #d1d5db",
            padding: "0.5rem 1rem",
            textAlign: "left",
            fontWeight: 600,
            backgroundColor: "#f3f4f6",
          }}
        >
          {children}
        </th>
      ),
      td: ({ children }: any) => (
        <td style={{ border: "1px solid #d1d5db", padding: "0.5rem 1rem", textAlign: "left" }}>{children}</td>
      ),
    };
  }, [fs, textColor, executionSteps, artifacts, lookupByName, chatMessages, onArtifactClick, scenarioJobs]);

  return (
    <Box
      textAlign="left"
      ml={0}
      className="markdown-content"
      w="100%"
      maxW="100%"
      minW="0"
      sx={{
        wordBreak: "break-word",
        overflowWrap: "break-word",
        "& *": { maxWidth: "100%" },
      }}
    >
      <ReactMarkdown
        key={markdownRemountKey}
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={markdownComponents}
      >
        {normalizedContent}
      </ReactMarkdown>
    </Box>
  );
};
